import React, { useCallback, useContext, useEffect, useState } from 'react';
import ActionLink, { ACTION_LINK_HEIGHT, ActionLinkButton } from '../../../../Footer/ActionLink';
import { submissionToUpdateGraphQLInput } from '../../SubmitGame/submission-helpers';
import { toast } from 'react-toastify';
import { updateSubmission } from '../Mutations/update-submission-mutation';
import { SubmissionContext } from '../Context/SubmissionProvider';
import GameDetailsFormLoader from '../GameDetailsForm/GameDetailsFormLoader';
import GameBaseFormName from '../GameBaseForm/GameBaseFormName';
import { Box } from '@mui/material';
import { isDefined } from '@crazygames/utils/isDefined';
import moment from 'moment';
import { Submission } from '../submission-graphql';
import { SaveOutlined } from '@mui/icons-material';

interface FullEditStepProps {
  goToOverview(): void;
  goToQA(submission: Submission, gameBuildId: string): void;
  showOnlyPrimaryButton?: boolean;
}

const InfoUpdate: React.FC<FullEditStepProps> = ({ goToOverview, goToQA, showOnlyPrimaryButton = false }) => {
  const {
    baseForm,
    detailsForm,
    detailsFormProblems,
    baseFormProblems,
    submission,
    setAttemptedToSave,
    modified,
    setIsModified,
    updateSubmission: updateSubmissionInContext,
    resetContextState,
    isNonEditable,
    disableDetailsEditing,
  } = useContext(SubmissionContext);
  const [hasUpdates, setHasUpdates] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (modified) {
      setHasUpdates(true);
    }
  }, [modified]);

  const submit = useCallback(async () => {
    if ((detailsFormProblems && detailsFormProblems.length > 0) || (baseFormProblems && baseFormProblems.length > 0)) {
      console.error(`Form problems: ${baseFormProblems} ${detailsFormProblems}`);
      toast.error('Please check if all the mandatory fields marked with * are filled before continuing', { autoClose: 8000 });
      setAttemptedToSave(true);
      return;
    }

    setIsSaving(true);

    try {
      const updateInput = submissionToUpdateGraphQLInput(submission!, 'fullEdit', baseForm!, detailsForm);
      const result = await updateSubmission(updateInput);

      if (!result.errors) {
        setIsModified(false);
        toast.success('Your changes have been saved.');
      }

      if (result.data?.developerSubmissionUpdate) {
        updateSubmissionInContext(result.data?.developerSubmissionUpdate);
      }
    } catch (e) {
      console.error(e);
      const errorMsg = e instanceof Error ? e.message : 'UNKNOWN_ERROR';
      toast.error(errorMsg, { autoClose: 5000 });
    } finally {
      setIsSaving(false);
      setHasUpdates(false);
    }
  }, [
    baseFormProblems,
    baseForm,
    detailsForm,
    detailsFormProblems,
    setAttemptedToSave,
    setIsModified,
    submission,
    updateSubmissionInContext,
  ]);

  if (!isDefined(submission) || submission.gameBuilds.length === 0) {
    return;
  }
  const descendingBuilds = [...submission.gameBuilds].sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)));
  const latestBuild = descendingBuilds[0];

  /**
   * 1. If there are changes, return "Save" button
   * 2. If latest build is DRAFT, return the go to QA button. Various changes on this page (allow embed, fullscreen) can create a new build.
   */
  const getPrimaryButton = (): ActionLinkButton | undefined => {
    const BUTTONS = {
      save: {
        title: 'Save',
        callback: () => submit(),
        isLoading: isSaving,
        icon: <SaveOutlined />,
        tooltip: 'You need to make some changes to be able to save.',
      },
      go_to_qa: {
        title: 'Go to QA',
        callback: () => goToQA(submission, latestBuild.id),
      },
    };

    if (isNonEditable) {
      /** Don't use here disableDetailsEditing, as some details on InfoUpdate page (covers, videos, fullscreen) are still editable and should show save button */
      return undefined;
    }

    if (submission.status === 'NEW_SUBMISSION' || disableDetailsEditing || modified) {
      return BUTTONS.save;
    }

    if (latestBuild.status === 'DRAFT') {
      return BUTTONS.go_to_qa;
    }
  };

  return (
    <Box sx={{ paddingBottom: `${ACTION_LINK_HEIGHT}px` }}>
      <GameBaseFormName />
      <GameDetailsFormLoader />
      {submission.status !== 'REJECTED' && (
        <ActionLink
          secondaryButton={{
            title: 'Back to Overview',
            callback: () => {
              resetContextState();
              goToOverview();
            },
          }}
          primaryButtonDisabled={!hasUpdates}
          primaryButton={getPrimaryButton()}
          showOnlyPrimaryButton={showOnlyPrimaryButton}
        />
      )}
    </Box>
  );
};

export default InfoUpdate;
